import { useEffect, useState } from 'react'
import { sendKinesisLog } from '~/app/utils/kinesis'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import { DateTime } from 'luxon'
import { isTruthy } from '~/app/utils'
import { getLeisureProductCategoryForRequest } from '~/api/converters/domestic/order/ClientLogs/kinesis'

const transformProductOptions = (cartItems: CartItemsV3Query['cartItemsV3'], optionId?: string) => {
  return cartItems
    .map(item => {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        return item.options.map(option => {
          if (optionId && optionId !== option.id) {
            return null
          }
          return {
            propertyId: item.propertyId,
            roomTypeId: option.roomTypeId,
            roomTypeVersion: option.roomTypeVersion,
            useType: option.useType,
            price: String(option.price),
            checkInDate: DateTime.fromISO(option.checkInAt).toFormat('yyyy-MM-dd'),
            checkInTime: DateTime.fromISO(option.checkInAt).toFormat('HH:mm'),
            checkOutDate: DateTime.fromISO(option.checkOutAt).toFormat('yyyy-MM-dd'),
            checkOutTime: DateTime.fromISO(option.checkOutAt).toFormat('HH:mm'),
            ratePlanId: option.ratePlanId,
            ratePlanVersion: option.ratePlanVersion,
            surcharges: option.surcharges,
            adults: option.adults,
            child: option.child,
            availability: option.availability,
            unavailableReason: option.unavailableReason,
            outdated: option.outdated,
            remains: option.remains,
            badgeMemberCode: option.memberClass,
          }
        })
      } else if (item.__typename === 'DomesticLeisureCartItem') {
        return item.options.map(option => {
          if (optionId && optionId !== option.id) {
            return null
          }
          return {
            dealId: item.dealId,
            optionId: option.leisureOptionId,
            optionName: option.leisureOptionName,
            price: option.price,
            quantity: option.quantity,
            productType: getLeisureProductCategoryForRequest(option.product.productTypeCode),
            availability: option.availability,
            unavailableReason: option.unavailableReason,
            outdated: option.outdated,
            remains: option.remains,
            purchaseOptionTypeCode: option.product.purchaseOptionTypeCode,
            productOptionGroupName: option.variant.productOptionGroup.productOptionGroupName || '',
            productOptionGroupId: option.variant.productOptionGroup.productOptionGroupId,
          }
        })
      } else if (item.__typename === 'DomesticTrainCartItem') {
        return item.options.map(option => {
          if (optionId && optionId !== option.id) {
            return null
          }
          return {
            trainMeta: option.trains.map(ticket => {
              return {
                departureStationCode: ticket.departureStationCode,
                departureStation: ticket.departureStationName,
                arrivalStation: ticket.arrivalStationName,
                arrivalStationCode: ticket.arrivalStationCode,
                trainClassifyNo: item.stationTrainClassifyCode,
                trainNo: ticket.trainNo,
                roundtripLeg: option.trains.length > 1 ? 'return' : 'outbound',
                tripType: option.trains.length > 1 ? 'ROUND' : 'ONEWAY',
                trainSeatSet: (ticket?.seats || []).map(seat => ({
                  carriageNo: seat.carriageNo,
                  seatDescription: seat.seatDescription,
                  trainCarriageName: `${+seat.carriageNo}호차`,
                  trainSeatName: seat.seatDescription,
                })),
              }
            }),
            adults: option.adults,
            child: option.child,
            availability: option.availability,
            unavailableReason: option.unavailableReason,
            outdated: option.outdated,
            remains: option.remains,
          }
        })
      } else if (item.__typename === 'DomesticBusCartItem') {
        return item.options.map(option => {
          if (optionId && optionId !== option.id) {
            return null
          }
          return {
            busMeta: option.reservations.map(reservation => {
              return {
                returnTrip: reservation.returnTrip,
                departureDate: reservation.departureDate,
                departureTime: reservation.departureTime,
                departureTerminalNo: reservation.departureTerminalNo,
                arrivalTerminalNo: reservation.arrivalTerminalNo,
                allocationDepartureTerminalNo: reservation.allocationDepartureTerminalNo,
                allocationArrivalTerminalNo: reservation.allocationArrivalTerminalNo,
                transportationCompanyCode: reservation.transportationCompanyCode,
                busGrade: reservation.busGrade,
                seats: reservation.seats.map(seat => {
                  return {
                    seatNo: seat.seatNo,
                    ticketType: seat.ticketType,
                  }
                }),
                price: reservation.seats.reduce((acc, cur) => acc + +cur.price, 0),
                busDiscountAmount: 0,
              }
            }),
          }
        })
      }
    })
    .flat()
    .filter(v => v)
}

const sendCartPageView = (isLogin: boolean, cartItems: CartItemsV3Query['cartItemsV3']) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'view',
    isLogin: isLogin ? 'Y' : 'N',
    productOptions: transformProductOptions(cartItems),
  })
}

export const useCartPageView = (isLogin: boolean, data?: CartItemsV3Query['cartItemsV3']) => {
  const [sent, setSent] = useState(false)
  useEffect(() => {
    if (data && !sent) {
      sendCartPageView(isLogin, data)
      setSent(true)
    }
  }, [data])
}

export const sendKinesisFilterClick = (filterName: string, isLogin: boolean) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: filterName,
    objectType: 'filterName',
    isLogin: isLogin ? 'Y' : 'N',
  })
}

export const sendKinesisProductClick = async (
  isLogin: boolean,
  optionId: string,
  cartItems: CartItemsV3Query['cartItemsV3']
) => {
  const productOption = transformProductOptions(cartItems, optionId)[0]
  if (productOption) {
    await sendKinesisLog({
      pageName: 'Cart',
      eventType: 'click',
      object: 'cartProductOptionLink',
      isLogin: isLogin ? 'Y' : 'N',
      productOption,
    })
  }
}

export const sendKinesisChangeUseTimeClick = (useTime: string) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: useTime,
    objectType: 'changeUseTime',
  })
}

export const sendKinesisOrderClick = (
  isLogin: boolean,
  data: CartItemsV3Query['cartItemsV3'],
  chosenTrainPackageDiscountAmount: number,
  trackCode: string
) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: 'orderBtn',
    isLogin: isLogin ? 'Y' : 'N',
    productOptions: transformProductOptions(data),
    packageDiscountAmount: chosenTrainPackageDiscountAmount,
    trackCode,
  })
}

export const sendKinesisBannerClick = async (
  object: string,
  objectType: string,
  benefitBannerTitle: string,
  deepLinkUri: string
) => {
  await sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object,
    objectType,
    objectContainer: 'benefitBanner',
    benefitBannerTitle,
    deepLinkUri,
  })
}

export const sendKinesisBannerImpr = (
  object: string,
  objectType: string,
  benefitBannerTitle: string
) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'impr',
    object,
    objectType,
    objectContainer: 'benefitBanner',
    benefitBannerTitle,
  })
}

export const sendKinesisRemoveUnavailableCheckedCartItem = () => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: 'cartDelBtn',
  })
}

export const sendKinesisLoginClick = () => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: 'loginBtn',
  })
}

const transformPriceChangedItems = (cartItems: CartItemsV3Query['cartItemsV3']) => {
  return cartItems
    .map(item => {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        return item.options
          .filter(option => option.isChosen && option.itemUpdatedByMemberClass)
          .map(option => ({
            propertyId: item.propertyId,
            roomTypeId: option.roomTypeId,
            ratePlanId: option.ratePlanId,
            badgeMemberCode: option.memberClass,
          }))
      }
    })
    .flat()
    .filter(isTruthy)
}

export const sendKinesisVipBottomSheetImpr = (cartItems: CartItemsV3Query['cartItemsV3']) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'impr',
    object: 'priceChangeDownInfo',
    objectType: 'bottomSheet',
    objectContainer: 'priceChangeDownInfo',
    priceChangedItems: transformPriceChangedItems(cartItems),
  })
}

export const sendKinesisVipBottomSheetOrderClick = (cartItems: CartItemsV3Query['cartItemsV3']) => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: 'orderBtn',
    objectType: 'btn',
    objectContainer: 'priceChangeDownInfo',
    priceChangedItems: transformPriceChangedItems(cartItems),
  })
}

export const sendKinesisInvalidMemberPopupImpr = () => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'impr',
    object: 'priceChangeUpInfo',
    objectType: 'popup',
    objectContainer: 'priceChangeUpInfo',
  })
}

export const sendKinesisInvalidMemberPopupClick = () => {
  sendKinesisLog({
    pageName: 'Cart',
    eventType: 'click',
    object: 'confirmBtn',
    objectType: 'popup',
    objectContainer: 'priceChangeUpInfo',
  })
}
